import { useEffect } from "react";
import PropTypes from "prop-types";
import { TextField as MuiTextField } from "@mui/material";
import { ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";

/**
 * @callback TextOnChange
 * @param {event} e The event source of the callback
 * @param {Function} setValue Formik's value setter for this Field
 */

/**
 * A custom component for use with Formik's Field component that also wraps
 * Material UI's TextField component so we can take advantage of both tools.
 *
 * Formk handles passing `field` & `form` props, the remainder are passed as
 * a blind passthrough if specified on Formik's `<Field />`.
 *
 * @param {object} props
 * @param {object} props.field Formik's field value object -- { name, value, onChange, onBlur }
 * @param {object} props.form Formik's form object
 * @param {string} [props.label] A react-i18next translation file entry
 * @param {TextOnChange} [props.onChange] Optional onChange callback to be executed
 *        This custom onChange will be passed the field setter & is expected to manage the field state.
 * @param {boolean} [props.forceError] Optional flag to force error check on initial render.
 *        Useful for fields that may be initially hidden but error state should be shown right away.
 *        (e.g. an input is not initially rendered, but the value loaded for it is invalid)
 * @param {boolean} [props.forceHelperText] Optional flag to force the helper text to always display
 *        Useful for when you need to align multiple fields in a row and do not want the input to
 *        be shifted up higher than the other fields when an error message is shown.
 * @param {object} [props.styles] Any additional styles, passed to MUI's sx prop
 *
 * @see https://formik.org/docs/api/field#component
 * @see https://mui.com/material-ui/react-text-field/
 * @see https://mui.com/material-ui/react-text-field/#helper-text for more about forceHelperText
 */
const FormikTextField = ({
  field,
  form,
  label,
  onChange = null,
  forceError = false,
  forceHelperText = false,
  styles = {},
  ...otherProps
}) => {
  const { t } = useTranslation();
  const { touched, errors } = form;
  const { onChange: formikOnChange, ...fieldProps } = field;
  const { name } = fieldProps;
  const changeHandler = onChange ?? formikOnChange;
  const customOnChange = !!onChange;

  const hasError = () => {
    if (name.indexOf("[") > -1) {
      // Compound name for arrays of objects: e.g. directors[0].email
      const [arrayName, indexEtc] = name.split("[");
      const [index, key] = indexEtc.split("].");

      return (
        errors?.[arrayName]?.[index]?.[key] &&
        touched?.[arrayName]?.[index]?.[key]
      );
    }

    return errors[name] && touched[name];
  };

  // Used to force rendering the error state & validation warning from
  // Formik on initial render by programatically setting the touched state.
  useEffect(() => {
    if (forceError) {
      form.setFieldTouched(name);
    }
    // Force ignoring deps as we only want this run once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MuiTextField
      id={`formik-mui-text-field-${name}`}
      label={typeof label === "string" ? t(label) : label}
      onChange={(e) => {
        if (customOnChange) {
          // Get the field setter to inject into the custom onChange
          const { setValue } = form.getFieldHelpers(field.name);
          changeHandler(e, setValue);
        } else {
          formikOnChange(e);
        }
      }}
      {...fieldProps}
      error={hasError()}
      helperText={
        hasError() ? <ErrorMessage name={name} /> : forceHelperText ? " " : null
      }
      {...otherProps}
      sx={styles}
    />
  );
};

FormikTextField.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  label: PropTypes.node,
  onChange: PropTypes.func,
  forceError: PropTypes.bool,
  forceHelperText: PropTypes.bool,
  styles: PropTypes.object,
};

export default FormikTextField;
